import { motion } from "framer-motion";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { isMobile } from "react-device-detect";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LAND_TABS } from "../../../constants/marketPlace";
import {
  MarketPlaceLandTabsType,
  MarketPlaceMainTabsType,
} from "../../../types/pages/MarketPlace";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { EventConfig } from "../../../types/global";
import { Assets } from "../../../constants/assets";
import Countdown from "react-countdown";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

type Props = {};

const MarketPlaceTabs = (props: Props) => {
  const { t } = useAppTranslation(["marketplace"]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Track active tab index
  const [indicatorX, setIndicatorX] = useState(0); // TranslateX for the indicator
  const [indicatorWidth, setIndicatorWidth] = useState(50); // Width of the indicator
  const tabContainerRef = useRef<HTMLDivElement>(null); // Ref to track the container
  const eventConfig: EventConfig = useSelector((state: any) => state.events);
  const isEventShopOpen = eventConfig?.isOpen;
  const MAIN_TABS: MarketPlaceMainTabsType[] = useMemo(
    () => [
      {
        title: eventConfig?.title || t("marketplace:tabs.eventShop"),
        imageSrc: eventConfig?.iconUrl,
        type: 8,
        new: true,
        disabled: !isEventShopOpen,
        endTime: eventConfig?.endTime,
      },
      {
        title: t("marketplace:tabs.emberShop"),
        imageSrc: Assets.icons.ember1x1,
        type: 7,
        disabled: false,
      },
      {
        title: t("marketplace:tabs.arenaShop"),
        imageSrc: Assets.icons.arenaShop,
        type: 6,
        disabled: false,
      },
      {
        title: t("marketplace:tabs.lands"),
        type: 0,
        imageSrc:
          "https://core.chainoflegends.com/Admin/files/Photos/Lands/5/land-legendary.PNG",
      },
      {
        title: t("marketplace:tabs.stone"),
        type: 1,
        imageSrc: "/assets/images/icons/stone.png",
        className: "mrkt-stp6-2",
      },
      {
        title: t("marketplace:tabs.iron"),
        type: 2,
        imageSrc: "/assets/images/icons/metal.png",
      },
      {
        title: t("marketplace:tabs.troops"),
        type: 3,
        className: "mrkt-stp7",
        imageSrc:
          "https://core.chainoflegends.com/assets/troops/icon/warrior.png",
      },
      {
        title: t("marketplace:tabs.items"),
        type: 5,
        imageSrc: "/assets/images/icons/helmet-item.png",
      },
    ],
    [eventConfig, t]
  );
  const getLandTitle = (tab: MarketPlaceLandTabsType) => {
    switch (tab.id) {
      case 1:
        return t("marketplace:landTabs.desert");
      case 2:
        return t("marketplace:landTabs.common");
      case 3:
        return t("marketplace:landTabs.uncommon");
      case 4:
        return t("marketplace:landTabs.rare");
      case 5:
        return t("marketplace:landTabs.legendary");
      default:
        return tab.title;
    }
  };
  function handleChangeType(active: number, index: number) {
    navigate({
      pathname: "/marketplace",
      search: "type=" + active,
    });
    setActiveTabIndex(index); // Update the active tab index
  }

  function handleChangeTab(active: number) {
    navigate({
      pathname: "/marketplace",
      search: "id=" + active,
    });
  }

  const { type, landId } = React.useMemo(() => {
    const typeParam = searchParams.get("type");
    const landIdParam = searchParams.get("id");
    return {
      type: typeParam ? +typeParam : 0,
      landId: landIdParam ? +landIdParam : 1,
    };
  }, [searchParams]);

  useEffect(() => {
    const typeParam = searchParams.get("type");
    if (!typeParam) return setActiveTabIndex(1);
    setActiveTabIndex(+typeParam); // Update the active tab index
  }, []);

  useEffect(() => {
    // Dynamically set tab positions after the DOM is rendered
    const updateTabPosition = () => {
      const tabElements =
        tabContainerRef.current?.querySelectorAll(".marketplace-tab");
      if (tabElements && tabElements[activeTabIndex]) {
        const activeTab = tabElements[activeTabIndex] as HTMLElement;
        const rect = activeTab.getBoundingClientRect();
        const containerRect = tabContainerRef.current?.getBoundingClientRect();
        const offsetLeft = rect.left - (containerRect?.left || 0);
        setIndicatorX(offsetLeft + rect.width / 4);
        setIndicatorWidth(rect.width / 2);
      }
    };

    updateTabPosition();

    // Add an event listener for resize to update positions dynamically
    window.addEventListener("resize", updateTabPosition);

    return () => {
      window.removeEventListener("resize", updateTabPosition);
    };
  }, [activeTabIndex, type]);

  return (
    <>
      <motion.div
        initial={isMobile ? undefined : { opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        exit={{ opacity: 0 }}
        className="tab-bar mt-4"
      >
        <PerfectScrollbar style={{ maxWidth: "100%" }}>
          <div
            className="d-flex flex-nowrap m-0 p-0 justify-content-start justify-content-lg-center  position-relative"
            ref={tabContainerRef} // Reference to the tab container
          >
            {MAIN_TABS.map((tab: MarketPlaceMainTabsType, index: number) =>
              tab.disabled ? (
                <></>
              ) : (
                <button
                  onClick={(e) => {
                    if (tab.disabled) return e.preventDefault();
                    handleChangeType(tab.type, index);
                  }}
                  key={tab?.type}
                  className={classNames(
                    `marketplace-tab    pb-4 pt-3 position-relative px-md-4`,
                    tab.className,
                    {
                      active: tab.type === type,
                      "button-disabled": tab.disabled,
                    }
                  )}
                >
                  <div className="b-text ps-1">
                    <span className="text-light fs-med d-flex flex-column align-items-center fs-lg fs-sm-small">
                      <img
                        className="mb-1 circle fs-med"
                        alt={tab?.title}
                        src={tab.imageSrc}
                        width="55px"
                        height="55px"
                      />
                      {tab.new && !tab.disabled && (
                        <span
                          style={{ bottom: "35%", transform: "scale(.8)" }}
                          className="badge position-absolute bg-danger"
                        >
                          {t("marketplace:badges.new")}
                        </span>
                      )}
                      {tab.new && tab.disabled && (
                        <span className="badge position-absolute bottom-0 mb-1 bg-danger">
                          {t("marketplace:badges.comingSoon")}
                        </span>
                      )}
                      {tab.new && (
                        <span style={{border:"1px solid #867151",borderRadius:10 , height:20}} className=" position-absolute bottom-0 mb-1 me-1 p-0 px-2  d-flex align-items-center">
                          <Countdown
                            date={tab.endTime}
                            renderer={({ days, hours, minutes }) => (
                              <span style={{lineHeight:'50%'}} className="text-light   fs-tiny">
                                {days}
                                {t("marketplace:timeUnitsShort.days")},
                                {hours}
                                {t("marketplace:timeUnitsShort.hours")},
                                {minutes}
                                {t("marketplace:timeUnitsShort.minutes")}
                              </span>
                            )}
                          />
                        </span>
                      )}
                      {tab.title}
                    </span>
                  </div>
                </button>
              )
            )}
            {/* 
            {indicatorX !== null && (
              <motion.div
                className="position-absolute bg-dark-yellow"
                style={{
                  top: 0,
                  left: 0,
                  height: "4px",
                  borderBottomRightRadius: 5,
                  borderBottomLeftRadius: 5,
                }}
                animate={{
                  x: indicatorX, // Move the indicator based on the active tab's X position
                  width: indicatorWidth, // Adjust width based on the active tab's width
                }}
                transition={{ duration: 0.3 }}
              />
            )} */}
          </div>
        </PerfectScrollbar>
      </motion.div>

      {type === 0 && (
        <motion.div
          initial={isMobile ? undefined : { y: -125, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          exit={{ y: 200, opacity: 0 }}
          className="tab-bar mb-4"
          style={{ borderRadius: 0, marginTop: -5, zIndex: 10 }}
        >
          <PerfectScrollbar style={{ maxWidth: "100%" }}>
            <div className="d-flex flex-nowrap m-0 p-0   justify-content-start justify-content-md-center">
              {LAND_TABS.map((tab: MarketPlaceLandTabsType) => (
                <button
                  onClick={() => {
                    handleChangeTab(tab.id);
                  }}
                  key={tab?.id}
                  className={
                    ` marketplace-tab  px-md-4 land-tab pb-4 pt-3  ${tab.className} ` +
                    (tab.id === landId && "active")
                  }
                >
                  <div className="text-light ps-1  d-flex flex-column align-items-center  fs-lg fs-sm-small">
                    <img
                      className="me-0 me-sm-1 w-100"
                      alt={getLandTitle(tab)}
                      style={{ maxWidth: "55px", maxHeight: "55px" }}
                      src={tab.imageSrc}
                    />
                    <div className="mt-1 fs-med">{getLandTitle(tab)}</div>
                  </div>
                </button>
              ))}
            </div>
          </PerfectScrollbar>
        </motion.div>
      )}
    </>
  );
};

export default MarketPlaceTabs;
